import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Controller } from "react-hook-form";
import DatePicker from "react-datepicker";
import { Form, ListGroup } from "react-bootstrap";
import { CalendarEventFill } from "react-bootstrap-icons";
import moment from "moment";

import "./DatePickerStyles.css";

const BootDatePicker = ({
  label,
  name,
  control,
  isRequired,
  rules,
  onChange,
  defaultValue,
  isTable,
  errors,
  className,
  showTodayButton,
  disabled,
  language,
  popperProps,
  showDashboardPredefinedRanges,
  placeholder,
  showTimeSelect = false,
  showMonthYearPicker = false,
  selectsRange = false,
  showPredefinedRanges = false,
  ...props
}) => {
  const parentCellRef = useRef(null);
  const [inputValue, setInputValue] = useState("");

  const dateFormat = showMonthYearPicker
    ? "MM/yyyy"
    : showTimeSelect
      ? "dd-MM-yyyy hh:mm aa"
      : "dd-MM-yyyy";

  const displayFormat = showMonthYearPicker
    ? "MM-YYYY"
    : showTimeSelect
      ? "DD-MM-YYYY hh:mm A"
      : "DD-MM-YYYY";

  const handleCalendarOpen = useCallback(() => {
    const input = document.activeElement;
    if (!input) return;

    const foundParents = new Set();

    let current = input.parentElement;
    while (current && current !== document.body) {
      const style = window.getComputedStyle(current);
      if (["hidden", "auto", "scroll"].includes(style.overflow)) {
        foundParents.add(current);
      }
      current = current.parentElement;
    }

    if (foundParents.size > 0) {
      foundParents.forEach((cell) => {
        cell.dataset.originalOverflow = cell.style.overflow; // Store original
        cell.style.overflow = "visible";
      });
      parentCellRef.current = Array.from(foundParents);
    }
  }, []);

  const handleCalendarClose = useCallback(() => {
    if (Array.isArray(parentCellRef.current)) {
      parentCellRef.current.forEach((cell) => {
        if (cell?.style) {
          cell.style.overflow = cell.dataset.originalOverflow || ""; // Restore
          delete cell.dataset.originalOverflow;
        }
      });
    } else if (parentCellRef.current?.style) {
      parentCellRef.current.style.overflowY = "hidden";
      parentCellRef.current.style.overflowX = "auto";
    }
    parentCellRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      if (Array.isArray(parentCellRef.current)) {
        parentCellRef.current.forEach((cell) => {
          if (cell?.style) {
            cell.style.overflowY = "hidden";
            cell.style.overflowX = "hidden";
          }
        });
      } else if (parentCellRef.current?.style) {
        parentCellRef.current.style.overflowY = "hidden";
        parentCellRef.current.style.overflowX = "hidden";
      }
      parentCellRef.current = null;
    };
  }, []);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue || (selectsRange ? [null, null] : null)}
      rules={rules}
      render={({ field }) => {
        const error = errors?.[name];

        // Sync input value with field value
        if (!selectsRange) {
          if (field?.value && moment(field.value).isValid()) {
            const formatted = moment(field.value).format(displayFormat);
            if (formatted !== inputValue) {
              setInputValue(formatted);
            }
          } else if (inputValue !== "" && !field?.value) {
            setInputValue("");
          }
        } else {
          if (Array.isArray(field?.value)) {
            const [start, end] = field.value;
            let newVal = "";
            if (start && moment(start).isValid()) {
              newVal += moment(start).format(displayFormat);
            }
            if (end && moment(end).isValid()) {
              newVal += " - " + moment(end).format(displayFormat);
            }
            if (newVal !== inputValue) {
              setInputValue(newVal);
            }
          } else if (inputValue !== "" && !Array.isArray(field?.value)) {
            setInputValue("");
          }
        }

        let selected, startDate, endDate;
        let pickerOnChange;

        if (!selectsRange) {
          selected =
            field?.value && moment(field.value).isValid()
              ? moment(field.value).toDate()
              : null;
          pickerOnChange = (date) => {
            const formatted =
              date && moment(date).isValid()
                ? moment(date).format(displayFormat)
                : "";
            setInputValue(formatted);
            field?.onChange?.(date);
            onChange?.(date);
          };
        } else {
          const [start, end] = Array.isArray(field?.value)
            ? field.value
            : [null, null];
          startDate =
            start && moment(start).isValid() ? moment(start).toDate() : null;
          endDate = end && moment(end).isValid() ? moment(end).toDate() : null;
          pickerOnChange = (dates) => {
            const [newStart, newEnd] = dates;
            let formatted = "";
            if (newStart && moment(newStart).isValid()) {
              formatted += moment(newStart).format(displayFormat);
            }
            if (newEnd && moment(newEnd).isValid()) {
              formatted += " - " + moment(newEnd).format(displayFormat);
            }
            setInputValue(formatted);
            field?.onChange?.(dates);
            onChange?.(dates);
          };
        }

        const handleToday = () => {
          const today = new Date();
          const dates = [today, today];
          pickerOnChange(dates);
        };

        const handleLastWeek = () => {
          const today = new Date();
          const lastSunday = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate() - today.getDay() - 7
          );
          const lastSaturday = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate() - today.getDay() - 1
          );
          const dates = [lastSunday, lastSaturday];
          pickerOnChange(dates);
        };

        const handleThisWeek = () => {
          const today = new Date();
          const thisSunday = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate() - today.getDay()
          );
          const thisSaturday = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate() - today.getDay() + 6
          );
          const dates = [thisSunday, thisSaturday];
          pickerOnChange(dates);
        };

        const handleLastMonth = () => {
          const today = new Date();
          const lastMonthStart = new Date(
            today.getFullYear(),
            today.getMonth() - 1,
            1
          );
          const lastMonthEnd = new Date(
            today.getFullYear(),
            today.getMonth(),
            0
          );
          const dates = [lastMonthStart, lastMonthEnd];
          pickerOnChange(dates);
        };

        const handleThisMonth = () => {
          const today = new Date();
          const thisMonthStart = new Date(
            today.getFullYear(),
            today.getMonth(),
            1
          );
          const thisMonthEnd = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0
          );
          const dates = [thisMonthStart, thisMonthEnd];
          pickerOnChange(dates);
        };

        const handleLastYear = () => {
          const today = new Date();
          const lastYearStart = new Date(today.getFullYear() - 1, 0, 1);
          const lastYearEnd = new Date(today.getFullYear() - 1, 11, 31);
          const dates = [lastYearStart, lastYearEnd];
          pickerOnChange(dates);
        };

        const handleThisYear = () => {
          const today = new Date();
          const thisYearStart = new Date(today.getFullYear(), 0, 1);
          const thisYearEnd = new Date(today.getFullYear(), 11, 31);
          const dates = [thisYearStart, thisYearEnd];
          pickerOnChange(dates);
        };

        const calendarContainer =
          showPredefinedRanges && selectsRange
            ? ({ children }) => (
              <div className="react-datepicker react-datepicker__predefined-ranges">
                <ListGroup
                  variant="flush"
                  className="react-datepicker__ranges"
                >
                  <ListGroup.Item action onClick={handleToday}>
                    Today
                  </ListGroup.Item>
                  <ListGroup.Item action onClick={handleLastWeek}>
                    Last Week
                  </ListGroup.Item>
                  <ListGroup.Item action onClick={handleThisWeek}>
                    This Week
                  </ListGroup.Item>
                  <ListGroup.Item action onClick={handleLastMonth}>
                    Last Month
                  </ListGroup.Item>
                  <ListGroup.Item action onClick={handleThisMonth}>
                    This Month
                  </ListGroup.Item>
                  <ListGroup.Item action onClick={handleLastYear}>
                    Last Year
                  </ListGroup.Item>
                  <ListGroup.Item action onClick={handleThisYear}>
                    This Year
                  </ListGroup.Item>
                </ListGroup>
                {children}
              </div>
            )
            : showDashboardPredefinedRanges && selectsRange ? ({ children }) => (
              <div className="react-datepicker react-datepicker__predefined-ranges">
                <ListGroup
                  variant="flush"
                  className="react-datepicker__ranges"
                >

                  <ListGroup.Item action onClick={handleLastMonth}>
                    Last Month
                  </ListGroup.Item>
                  <ListGroup.Item action onClick={handleThisMonth}>
                    This Month
                  </ListGroup.Item>
                  <ListGroup.Item action onClick={handleLastYear}>
                    Last Year
                  </ListGroup.Item>
                  <ListGroup.Item action onClick={handleThisYear}>
                    This Year
                  </ListGroup.Item>
                </ListGroup>
                {children}
              </div>) : undefined;

        return (
          <Form.Group className={`position-relative`}>
            {label && (
              <Form.Label className="form-label-sm mb-2">
                {label}
                {isRequired && <span className="text-danger ms-1">*</span>}
              </Form.Label>
            )}

            <div className="position-relative">
              <DatePicker
                {...props}
                selected={!selectsRange ? selected : undefined}
                selectsRange={selectsRange}
                startDate={selectsRange ? startDate : undefined}
                endDate={selectsRange ? endDate : undefined}
                onChange={pickerOnChange}
                dateFormat={dateFormat}
                showMonthYearPicker={showMonthYearPicker}
                showTimeSelect={showTimeSelect}
                showMonthDropdown
                showYearDropdown
                popperProps={popperProps}
                dropdownMode="select"
                placeholderText={placeholder || "dd-mm-yyyy"}
                onCalendarOpen={handleCalendarOpen}
                onCalendarClose={handleCalendarClose}
                disabled={disabled}
                todayButton={showTodayButton && "Today"}
                isClearable={props.isClearable ?? true}
                // wrapperClassName="w-100"
                calendarStartDay={0}
                calendarContainer={calendarContainer}
                className={`form-control form-control-sm ${error ? "is-invalid" : ""
                  } ${className} `}
              />

              {/* Show icon only if no date is selected */}
              {!selected && !selectsRange && (
                <CalendarEventFill
                  className="position-absolute top-50 end-0 text-primary translate-middle-y me-2 "
                  style={{ pointerEvents: "none" }}
                />
              )}

              {/* For range picker: show icon only if start & end are not set */}
              {selectsRange && !startDate && !endDate && (
                <CalendarEventFill
                  className="position-absolute top-50 end-0 text-primary translate-middle-y me-2 "
                  style={{ pointerEvents: "none" }}
                />
              )}
            </div>
          </Form.Group>
        );
      }}
    />
  );
};

BootDatePicker.propTypes = {
  showTimeSelect: PropTypes.bool,
  showMonthYearPicker: PropTypes.bool,
  selectsRange: PropTypes.bool,
  showPredefinedRanges: PropTypes.bool,
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  rules: PropTypes.object,
  defaultValue: PropTypes.any,
  isTable: PropTypes.bool,
  errors: PropTypes.object,
  isRequired: PropTypes.bool,
  onChange: PropTypes.func,
  language: PropTypes.oneOf(["arabic", "english"]),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
};

BootDatePicker.defaultProps = {
  label: "",
  showTimeSelect: false,
  showMonthYearPicker: false,
  selectsRange: false,
  showPredefinedRanges: false,
  defaultValue: null,
  showTodayButton: true,
  isRequired: false,
  language: "english",
  className: "",
  disabled: false,
  placeholder: "",
};

export default BootDatePicker;
